import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer,} from "react";
import RecipeData = App.Data.RecipeData;

type FavoriteRecipesState = {
  favoriteUuids: string[];
};

type FavoriteRecipesAction =
  | {
  type: "RESET";
  favoriteUuids: Array<string | number> | null | undefined;
}
  | { type: "SET_FAVORITE"; uuid: string; favorite: boolean }
  | { type: "REMOVE_BY_UUID"; uuid: string };

interface FavoriteRecipesContextValue {
  favoriteUuids: string[];
  isFavorite: (recipeOrUuid: RecipeData | string) => boolean;
  setFavorite: (recipeOrUuid: RecipeData | string, favorite: boolean) => void;
  addFavorite: (recipeOrUuid: RecipeData | string) => void;
  removeFavorite: (recipeOrUuid: RecipeData | string) => void;
}

interface FavoriteRecipesProviderProps {
  initialFavoriteUuids?: Array<string | number> | null;
  children: ReactNode;
}

const FavoriteRecipesContext = createContext<
  FavoriteRecipesContextValue | undefined
>(undefined);

const ensureUnique = (uuids: string[]): string[] => Array.from(new Set(uuids));

const normalizeFavoriteUuids = (
  favoriteUuids?: Array<string | number> | null,
): string[] => {
  if (!favoriteUuids) {
    return [];
  }

  return favoriteUuids.map((uuid) => String(uuid));
};

const reducer = (
  state: FavoriteRecipesState,
  action: FavoriteRecipesAction,
): FavoriteRecipesState => {
  switch (action.type) {
    case "RESET": {
      const normalized = normalizeFavoriteUuids(action.favoriteUuids);
      return {
        favoriteUuids: ensureUnique(normalized),
      };
    }
    case "SET_FAVORITE": {
      const {uuid, favorite} = action;

      if (favorite) {
        if (state.favoriteUuids.includes(uuid)) {
          return state;
        }

        return {
          favoriteUuids: [...state.favoriteUuids, uuid],
        };
      }

      if (!state.favoriteUuids.includes(uuid)) {
        return state;
      }

      return {
        favoriteUuids: state.favoriteUuids.filter((value) => value !== uuid),
      };
    }
    case "REMOVE_BY_UUID": {
      if (!state.favoriteUuids.includes(action.uuid)) {
        return state;
      }

      return {
        favoriteUuids: state.favoriteUuids.filter(
          (value) => value !== action.uuid,
        ),
      };
    }
    default:
      return state;
  }
};

const createFavoriteUuidKey = (
  favoriteUuids?: Array<string | number> | null,
): string => {
  const normalized = normalizeFavoriteUuids(favoriteUuids);

  if (!normalized.length) {
    return "";
  }

  return [...normalized].sort().join("|");
};

export const FavoriteRecipesProvider = (
  {
    initialFavoriteUuids,
    children,
  }: FavoriteRecipesProviderProps) => {
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => ({
      favoriteUuids: ensureUnique(
        normalizeFavoriteUuids(initialFavoriteUuids),
      ),
    }),
  );

  const favoriteUuidKey = useMemo(
    () => createFavoriteUuidKey(initialFavoriteUuids),
    [initialFavoriteUuids],
  );

  useEffect(() => {
    dispatch({
      type: "RESET",
      favoriteUuids: initialFavoriteUuids,
    });
  }, [favoriteUuidKey]);

  const toUuid = useCallback((recipeOrUuid: RecipeData | string): string => {
    return typeof recipeOrUuid === "string"
      ? recipeOrUuid
      : String(recipeOrUuid.uuid);
  }, []);

  const isFavorite = useCallback(
    (recipeOrUuid: RecipeData | string) => {
      const uuid = toUuid(recipeOrUuid);
      return state.favoriteUuids.includes(uuid);
    },
    [state.favoriteUuids, toUuid],
  );

  const setFavorite = useCallback(
    (recipeOrUuid: RecipeData | string, favorite: boolean) => {
      const uuid = toUuid(recipeOrUuid);
      dispatch({type: "SET_FAVORITE", uuid, favorite});
    },
    [toUuid],
  );

  const addFavorite = useCallback(
    (recipeOrUuid: RecipeData | string) => {
      setFavorite(recipeOrUuid, true);
    },
    [setFavorite],
  );

  const removeFavorite = useCallback(
    (recipeOrUuid: RecipeData | string) => {
      const uuid = toUuid(recipeOrUuid);
      dispatch({type: "REMOVE_BY_UUID", uuid});
    },
    [toUuid],
  );

  const value = useMemo<FavoriteRecipesContextValue>(
    () => ({
      favoriteUuids: state.favoriteUuids,
      isFavorite,
      setFavorite,
      addFavorite,
      removeFavorite,
    }),
    [state.favoriteUuids, isFavorite, setFavorite, addFavorite, removeFavorite],
  );

  return (
    <FavoriteRecipesContext.Provider value={value}>
      {children}
    </FavoriteRecipesContext.Provider>
  );
};

export const useFavoriteRecipes = (): FavoriteRecipesContextValue => {
  const context = useContext(FavoriteRecipesContext);

  if (!context) {
    throw new Error(
      "useFavoriteRecipes must be used within a FavoriteRecipesProvider",
    );
  }

  return context;
};
